/**
 * ความสัมพันธ์หนึ่งทิศทางของ lot หนึ่งใบ — หนึ่ง hop เท่านั้น (D-073)
 *
 * **ไม่มีการกางต้นไม้ทั้งสายที่นี่ และต้องไม่มี** Phase 1 เป็น single-level:
 * endpoint คืน edge ตรงเท่านั้น ส่วนการไล่ต่อเป็นการกระทำของคน — lot ที่เกี่ยวข้อง
 * ทุกบรรทัดเป็นลิงก์ที่โหลดหน้าจอนี้ใหม่ให้ lot นั้น ผู้ใช้จึงเดินกราฟทีละก้าวโดยเห็น
 * เสมอว่าตอนนี้ยืนอยู่ที่ lot ไหน การกางอัตโนมัติจะเป็นการยิงคำขอเป็นทวีคูณเพื่อ
 * สร้างภาพที่ backend ไม่เคยรับประกันว่าครบ (multi-level เป็น Phase 2)
 */

import { Link, useLocation } from 'react-router-dom';

import { LotGenealogyFailureNotice } from './LotGenealogyFailureNotice';
import {
  directionCopy,
  lotTraceabilitySearch,
  multipleEdgesNote,
  qualityStatusLabel,
  qualityStatusTone,
} from './lotTraceabilityView';
import type { LotGenealogyState } from './useLotGenealogy';
import { InfoHint } from '../components/InfoHint';
import { StatusBadge } from '../StatusBadge';
import type { GenealogyDirection } from '../../lib/lotTraceability/types';

export interface LotGenealogyPanelProps {
  readonly direction: GenealogyDirection;
  /** lot ที่ถูกถาม — ไม่ใช่ lot ที่อยู่ในแต่ละบรรทัด */
  readonly lotId: string;
  readonly state: LotGenealogyState;
  readonly onLoadMore: () => void;
  readonly onRetry: () => void;
}

export function LotGenealogyPanel({
  direction,
  lotId,
  state,
  onLoadMore,
  onRetry,
}: LotGenealogyPanelProps) {
  const { pathname } = useLocation();
  const copy = directionCopy(direction);
  const edges = state.status === 'ready' ? state.edges : [];
  const mergeNote = multipleEdgesNote(direction, edges.length);

  return (
    <section
      className="cap-card cap-card--table"
      data-testid={`lot-genealogy-${direction}`}
      data-direction={direction}
      aria-label={copy.title}
    >
      <div className="cap-card__head">
        <h2 className="cap-card__title">
          {copy.title} <InfoHint label={`เกี่ยวกับ${copy.title}`} text={copy.purpose} />
        </h2>
      </div>

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid={`lot-genealogy-${direction}-loading`} role="status">
          <p className="cap-notice__text">กำลังโหลดความสัมพันธ์ของ lot…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <LotGenealogyFailureNotice
          failure={state}
          lotId={lotId}
          testId={`lot-genealogy-${direction}-failure`}
          onRetry={onRetry}
        />
      ) : null}

      {state.status === 'ready' && edges.length === 0 ? (
        <div className="cap-notice" data-testid={`lot-genealogy-${direction}-empty`} role="status">
          <StatusBadge tone="neutral">ไม่มีความสัมพันธ์</StatusBadge>
          <h3 className="cap-notice__title">{copy.emptyTitle}</h3>
          <p className="cap-notice__text">{copy.emptyText}</p>
        </div>
      ) : null}

      {state.status === 'ready' && edges.length > 0 ? (
        <>
          {mergeNote === null ? null : (
            <p className="cap-card__note" data-testid={`lot-genealogy-${direction}-multi`}>
              {mergeNote}
            </p>
          )}
          <div className="cap-table-wrap">
            <table className="cap-table">
              <caption className="cap-visually-hidden">
                {copy.title} — หนึ่งแถวคือหนึ่งความสัมพันธ์ตรงหนึ่งก้าว พร้อมสินค้า สถานะคุณภาพ
                จำนวนที่ถูกใช้ และรหัสการยืนยันการผลิตที่บันทึกไว้
              </caption>
              <thead>
                <tr>
                  <th scope="col">{copy.relatedLotHeading}</th>
                  <th scope="col">สินค้า</th>
                  <th scope="col">สถานะคุณภาพ</th>
                  <th scope="col">{copy.quantityHeading}</th>
                  <th scope="col">หน่วย</th>
                  <th scope="col">รหัสการยืนยันการผลิต</th>
                </tr>
              </thead>
              <tbody>
                {edges.map((edge) => (
                  <tr
                    key={`${edge.lot_id}-${edge.operation_confirmation_id}`}
                    data-testid={`lot-genealogy-${direction}-row-${edge.lot_id}`}
                    data-quality-status={edge.quality_status}
                  >
                    <th scope="row" className="cap-table__row-head">
                      {/* ลิงก์ = หนึ่งก้าวถัดไป path เดิม เปลี่ยนเฉพาะ lot ที่ถูกถาม */}
                      <Link
                        className="cap-table__link"
                        data-testid={`lot-genealogy-${direction}-follow-${edge.lot_id}`}
                        to={{ pathname, search: lotTraceabilitySearch(edge.lot_id) }}
                      >
                        {edge.lot_id}
                      </Link>
                    </th>
                    <td className="cap-table__code">{edge.product_code}</td>
                    <td>
                      <StatusBadge tone={qualityStatusTone(edge.quality_status)}>
                        {qualityStatusLabel(edge.quality_status)}
                      </StatusBadge>
                    </td>
                    {/* NUMERIC เดินทางเป็น string ตลอด — ไม่จัดรูปแบบซ้ำที่นี่ */}
                    <td className="cap-table__code">{edge.qty_consumed}</td>
                    <td className="cap-table__code">{edge.uom}</td>
                    <td className="cap-table__code">{edge.operation_confirmation_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cap-card__foot">
            {state.nextCursor === null ? (
              <span data-testid={`lot-genealogy-${direction}-complete`}>
                แสดงครบทุกความสัมพันธ์ตรงของ lot นี้แล้ว
              </span>
            ) : (
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid={`lot-genealogy-${direction}-more`}
                onClick={onLoadMore}
                disabled={state.loadingMore}
              >
                {state.loadingMore ? 'กำลังโหลดเพิ่ม…' : 'โหลดความสัมพันธ์ถัดไป'}
              </button>
            )}
          </div>
        </>
      ) : null}

      {state.status === 'ready' && state.loadMoreFailure !== null ? (
        <LotGenealogyFailureNotice
          failure={state.loadMoreFailure}
          lotId={lotId}
          testId={`lot-genealogy-${direction}-more-failure`}
        />
      ) : null}
    </section>
  );
}
